import { StateObject } from "@/dtos/State.dto";
import Billing from "@/services/billing/billing";
import Subscription from "@/services/billing/subscription";
import {
  CLEAR_SUBSCRIPTION_ISLOADING,
  CREATE_CHECKOUT,
  GET_ORGANIZATION_ACTIVE_SUBSCRIPTION,
  GET_ORGANIZATION_SUBSCRIPTIONS,
  GET_STRIPE_BILLING_PORTAL_URL,
  GET_STRIPE_SESSION_INFO,
  GET_SUBSCRIPTION_STATUS,
  GET_TRIAL_STATUS,
  START_FREE_TRIAL,
} from "@/store/action-types";
import {
  SET_ERRORMSG,
  SET_HAS_COMPLETED_ONBOARDING,
  SET_ORGANIZATION_ACTIVE_SUBSCRIPTION,
  SET_ORGANIZATION_SUBSCRIPTIONS,
  SET_STRIPE_BILLING_PORTAL_URL,
  SET_STRIPE_SESSION_INFO,
  SET_SUBSCRIPTION,
  SET_SUBSCRIPTION_STATUS,
  SET_SUBSCRIPTIONS,
  SET_TRIAL_STATUS,
  INVALIDATE_SUBSCRIPTION_CACHE,
} from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

export interface SubscriptionDiscount {
  code: string | null;
  percentOff: number | null;
  amountOffCents: number | null;
  duration: string | null;
  durationInMonths: number | null;
  endsAt: string | null;
  start: string | null;
}

export interface SubscriptionStatusData {
  status: string;
  planName: string;
  trialEndsAt: string | null;
  hasPaymentMethod: boolean;
  paymentMethodsCount: number;
  currentPeriodEnd: string | null;
  currentPeriodStart: string | null;
  hasSubscription: boolean;
  monthlyCostCents: number;
  discount: SubscriptionDiscount | null;
}

export interface TrialStatusData {
  type: "stripe_subscription" | "free_trial" | "none";
  status: string;
  trialEndsAt: Date | null;
  daysRemaining?: number;
  isPaid?: boolean;
  isViewOnly?: boolean;
}

const CACHE_TTL = 60 * 1000;

// In-flight promise deduplication
let _subscriptionStatusPromise: Promise<any> | null = null;
let _orgActiveSubPromise: Promise<any> | null = null;
let _trialStatusPromise: Promise<any> | null = null;

const subscription: Module<any, {}> = {
  state: {
    subscription: {
      data: null,
      isLoading: false,
    },
    subscriptions: {
      data: null,
      isLoading: false,
    },
    organizationActiveSubscription: {
      data: null,
      isLoading: false,
      cacheTimestamp: null,
    },
    organizationSubscriptions: {
      data: null,
      isLoading: false,
    },
    stripeSessionInfo: {
      data: null,
      isLoading: false,
    },
    stripeBillingPortalUrl: {
      data: null,
      isLoading: false,
    },
    subscriptionStatus: {
      data: null as SubscriptionStatusData | null,
      isLoading: false,
      cacheTimestamp: null as number | null,
    },
    trialStatus: {
      data: null as TrialStatusData | null,
      isLoading: false,
      cacheTimestamp: null as number | null,
    },
    hasCompletedOnboarding: false,
  },
  getters: {
    subscription: (state: { subscription: any }) => state.subscription,
    subscriptions: (state: { subscriptions: any }) => state.subscriptions,
    stripeSessionInfo: (state: { stripeSessionInfo: any }) => state.stripeSessionInfo,
    organizationActiveSubscription: (state: { organizationActiveSubscription: any }) =>
      state.organizationActiveSubscription,
    organizationSubscriptions: (state: { organizationSubscriptions: any }) =>
      state.organizationSubscriptions,
    stripeBillingPortalUrl: (state: { stripeBillingPortalUrl: any }) =>
      state.stripeBillingPortalUrl,
    subscriptionStatus: (state: { subscriptionStatus: any }) => state.subscriptionStatus,
    isTrialing: (state: { subscriptionStatus: any }) =>
      state.subscriptionStatus.data?.status === "trialing",
    hasPaymentMethod: (state: { subscriptionStatus: any }) =>
      state.subscriptionStatus.data?.hasPaymentMethod ?? false,
    trialEndsAt: (state: { subscriptionStatus: any }) =>
      state.subscriptionStatus.data?.trialEndsAt ?? null,
    daysLeftInTrial: (state: { subscriptionStatus: any }) => {
      const trialEndsAt = state.subscriptionStatus.data?.trialEndsAt;
      if (!trialEndsAt) return 0;
      const now = new Date();
      const trialEnd = new Date(trialEndsAt);
      const diffTime = trialEnd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    },
    trialStatus: (state: { trialStatus: any }) => state.trialStatus,
    hasCompletedOnboarding: (state: { hasCompletedOnboarding: boolean }) =>
      state.hasCompletedOnboarding,
    isViewOnlyMode: (state: { trialStatus: any }) => {
      const trialData = state.trialStatus.data;
      return trialData?.type === "free_trial" && trialData?.status === "expired";
    },
    isFreeTrialActive: (state: { trialStatus: any }) => {
      const trialData = state.trialStatus.data;
      return trialData?.type === "free_trial" && trialData?.status === "active";
    },
    // Access-control fallback: free trial with days remaining but a non-"active" status
    // string (e.g. "trialing"). Does NOT apply when status is "expired" (view-only state).
    hasFreeTrialDaysLeft: (state: { trialStatus: any }) => {
      const trialData = state.trialStatus.data;
      if (!trialData || trialData.type !== "free_trial") return false;
      if (trialData.status === "expired") return false;
      if (typeof trialData.daysRemaining === "number") return trialData.daysRemaining > 0;
      if (trialData.trialEndsAt) return new Date(trialData.trialEndsAt) > new Date();
      return false;
    },
  },
  mutations: {
    [SET_SUBSCRIPTION]: (
      state: { subscription: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.subscription.data = payload.data;
      state.subscription.isLoading = payload.isLoading;
    },
    [SET_SUBSCRIPTIONS]: (
      state: { subscriptions: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.subscriptions.data = payload.data;
      state.subscriptions.isLoading = payload.isLoading;
    },
    [SET_ORGANIZATION_ACTIVE_SUBSCRIPTION]: (
      state: { organizationActiveSubscription: any },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      state.organizationActiveSubscription.data = payload.data;
      state.organizationActiveSubscription.isLoading = payload.isLoading;
      if (payload.data !== null) {
        state.organizationActiveSubscription.cacheTimestamp = Date.now();
      }
    },
    [SET_ORGANIZATION_SUBSCRIPTIONS]: (
      state: { organizationSubscriptions: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      state.organizationSubscriptions.data = payload.data;
      state.organizationSubscriptions.isLoading = payload.isLoading;
    },
    [SET_STRIPE_SESSION_INFO]: (
      state: { stripeSessionInfo: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.stripeSessionInfo.data = payload.data;
      state.stripeSessionInfo.isLoading = payload.isLoading;
    },
    [SET_STRIPE_BILLING_PORTAL_URL]: (
      state: { stripeBillingPortalUrl: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.stripeBillingPortalUrl.data = payload.data;
      state.stripeBillingPortalUrl.isLoading = payload.isLoading;
    },
    [INVALIDATE_SUBSCRIPTION_CACHE]: (state: {
      organizationActiveSubscription: any;
      subscriptionStatus: any;
      trialStatus: any;
    }) => {
      state.organizationActiveSubscription.cacheTimestamp = null;
      state.subscriptionStatus.cacheTimestamp = null;
      state.trialStatus.cacheTimestamp = null;
    },
    [SET_SUBSCRIPTION_STATUS]: (
      state: { subscriptionStatus: any },
      payload: { data: SubscriptionStatusData | null; isLoading: boolean }
    ) => {
      if (payload.data !== undefined) {
        state.subscriptionStatus.data = payload.data;
      }
      state.subscriptionStatus.isLoading = payload.isLoading;
      if (payload.data !== null && payload.data !== undefined) {
        state.subscriptionStatus.cacheTimestamp = Date.now();
      }
    },
    [SET_TRIAL_STATUS]: (
      state: { trialStatus: any },
      payload: { data: TrialStatusData | null; isLoading: boolean }
    ) => {
      if (payload.data !== undefined) {
        state.trialStatus.data = payload.data;
      }
      state.trialStatus.isLoading = payload.isLoading;
      if (payload.data !== null && payload.data !== undefined) {
        state.trialStatus.cacheTimestamp = Date.now();
      }
    },
    [SET_HAS_COMPLETED_ONBOARDING]: (
      state: { hasCompletedOnboarding: boolean },
      payload: boolean
    ) => {
      state.hasCompletedOnboarding = payload;
    },
  },
  actions: {
    [CREATE_CHECKOUT]: async ({ commit }: any, payload: any) => {
      commit(SET_SUBSCRIPTION, { data: null, isLoading: true });

      const priceId = typeof payload === "string" ? payload : payload?.priceId;
      const promoCode = typeof payload === "string" ? undefined : payload?.promoCode;
      const result: AxiosResponse = await Subscription.createCheckout(priceId, promoCode);

      if (result) {
        if (result.data.status) {
          commit(SET_SUBSCRIPTION, {
            data: result.data.data,
            isLoading: false,
          });
          return result.data.data;
        }
        commit(SET_SUBSCRIPTION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SUBSCRIPTION, { isLoading: false });
        return false;
      }
    },
    [GET_STRIPE_SESSION_INFO]: async ({ commit }: any, payload: any) => {
      commit(SET_STRIPE_SESSION_INFO, { data: null, isLoading: true });

      const result: AxiosResponse = await Subscription.getStripeSessionInfo(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_STRIPE_SESSION_INFO, {
            data: result.data.data,
            isLoading: false,
          });
          return result;
        }
        commit(SET_STRIPE_SESSION_INFO, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_STRIPE_SESSION_INFO, { isLoading: false });
        return false;
      }
    },
    [GET_ORGANIZATION_ACTIVE_SUBSCRIPTION]: async ({ commit, state }: any, payload: any) => {
      const cachedData = state.organizationActiveSubscription.data;
      const cacheTimestamp = state.organizationActiveSubscription.cacheTimestamp;
      const now = Date.now();
      const forceRefresh = payload?.forceRefresh === true;

      if (!forceRefresh && cachedData && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
        return {
          data: {
            status: true,
            data: cachedData,
          },
        };
      }

      if (!forceRefresh && _orgActiveSubPromise) return _orgActiveSubPromise;

      _orgActiveSubPromise = (async () => {
        commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, {
          isLoading: true,
        });

        try {
          const result: AxiosResponse = await Billing.getSubscriptionStatus();

          if (result?.data?.status) {
            const rawData = result.data.data;
            if (rawData.has_subscription === false && rawData.hasSubscription === undefined) {
              commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, { data: null, isLoading: false });
              // Still cross-populate subscriptionStatus so GET_SUBSCRIPTION_STATUS hits cache
              const normalizedData: SubscriptionStatusData = {
                status: rawData.status,
                planName: rawData.plan || rawData.planName || rawData.plan_name || null,
                trialEndsAt: rawData.trial_ends_at || rawData.trialEndsAt || null,
                hasPaymentMethod: rawData.has_payment_method ?? rawData.hasPaymentMethod ?? false,
                paymentMethodsCount:
                  rawData.payment_methods_count ?? rawData.paymentMethodsCount ?? 0,
                currentPeriodEnd: rawData.current_period_end || rawData.currentPeriodEnd || null,
                currentPeriodStart:
                  rawData.current_period_start || rawData.currentPeriodStart || null,
                hasSubscription: false,
                monthlyCostCents: rawData.monthly_cost_cents ?? rawData.monthlyCostCents ?? 0,
                discount: rawData.discount ?? null,
              };
              commit(SET_SUBSCRIPTION_STATUS, { data: normalizedData, isLoading: false });
              return false;
            }

            const subscriptionData = {
              status: rawData.status,
              planName: rawData.plan || rawData.planName || rawData.plan_name || null,
              trialEndsAt: rawData.trial_ends_at || rawData.trialEndsAt || null,
              currentPeriodEnd: rawData.current_period_end || rawData.currentPeriodEnd || null,
              hasPaymentMethod: rawData.has_payment_method ?? rawData.hasPaymentMethod ?? false,
            };
            commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, {
              data: subscriptionData,
              isLoading: false,
            });

            // Cross-populate subscriptionStatus cache to avoid duplicate API calls
            const normalizedData: SubscriptionStatusData = {
              status: rawData.status,
              planName: rawData.plan || rawData.planName || rawData.plan_name || null,
              trialEndsAt: rawData.trial_ends_at || rawData.trialEndsAt || null,
              hasPaymentMethod: rawData.has_payment_method ?? rawData.hasPaymentMethod ?? false,
              paymentMethodsCount:
                rawData.payment_methods_count ?? rawData.paymentMethodsCount ?? 0,
              currentPeriodEnd: rawData.current_period_end || rawData.currentPeriodEnd || null,
              currentPeriodStart:
                rawData.current_period_start || rawData.currentPeriodStart || null,
              hasSubscription: rawData.has_subscription ?? rawData.hasSubscription ?? false,
              monthlyCostCents: rawData.monthly_cost_cents ?? rawData.monthlyCostCents ?? 0,
              discount: rawData.discount ?? null,
            };
            commit(SET_SUBSCRIPTION_STATUS, { data: normalizedData, isLoading: false });

            return {
              data: {
                status: true,
                data: subscriptionData,
              },
            };
          }
          commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, {
            data: null,
            isLoading: false,
          });
          return false;
        } catch (error: any) {
          commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, { isLoading: false });
          if (error?.response?.status === 401 || error?.response?.status === 403) return null;
          throw error;
        }
      })();
      _orgActiveSubPromise.finally(() => {
        _orgActiveSubPromise = null;
      });
      return _orgActiveSubPromise;
    },
    [GET_ORGANIZATION_SUBSCRIPTIONS]: async ({ commit }: any) => {
      commit(SET_ORGANIZATION_SUBSCRIPTIONS, { data: [], isLoading: false });
      return { data: { status: true, data: [] } };
    },
    [GET_STRIPE_BILLING_PORTAL_URL]: async ({ commit }: any, payload: any) => {
      commit(SET_STRIPE_BILLING_PORTAL_URL, { data: null, isLoading: true });

      const result: AxiosResponse = await Subscription.getStripeBillingPortalURL(payload?.flow);

      if (result) {
        if (result.data.status) {
          commit(SET_STRIPE_BILLING_PORTAL_URL, {
            data: result.data.data,
            isLoading: false,
          });
          return result.data.data;
        }
        commit(SET_STRIPE_BILLING_PORTAL_URL, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_STRIPE_BILLING_PORTAL_URL, { isLoading: false });
        return false;
      }
    },
    [CLEAR_SUBSCRIPTION_ISLOADING]: ({ commit }) => {
      commit(SET_SUBSCRIPTION, { isLoading: false });
      commit(SET_SUBSCRIPTIONS, { isLoading: false });
      commit(SET_ORGANIZATION_ACTIVE_SUBSCRIPTION, { isLoading: false });
      commit(SET_ORGANIZATION_SUBSCRIPTIONS, { isLoading: false });
      commit(SET_STRIPE_SESSION_INFO, { isLoading: false });
      commit(SET_STRIPE_BILLING_PORTAL_URL, { isLoading: false });
    },
    [GET_SUBSCRIPTION_STATUS]: async (
      { commit, state }: any,
      payload?: { forceRefresh?: boolean }
    ) => {
      const cachedData = state.subscriptionStatus.data;
      const cacheTimestamp = state.subscriptionStatus.cacheTimestamp;
      const now = Date.now();
      const forceRefresh = payload?.forceRefresh === true;

      if (!forceRefresh && cachedData && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
        return cachedData;
      }

      if (!forceRefresh && _subscriptionStatusPromise) return _subscriptionStatusPromise;

      _subscriptionStatusPromise = (async () => {
        commit(SET_SUBSCRIPTION_STATUS, { isLoading: true });

        try {
          const result: AxiosResponse = await Billing.getSubscriptionStatus();
          if (result?.data?.status) {
            const rawData = result.data.data;
            const normalizedData: SubscriptionStatusData = {
              status: rawData.status,
              planName: rawData.plan || rawData.planName || rawData.plan_name || null,
              trialEndsAt: rawData.trial_ends_at || rawData.trialEndsAt || null,
              hasPaymentMethod: rawData.has_payment_method ?? rawData.hasPaymentMethod ?? false,
              paymentMethodsCount:
                rawData.payment_methods_count ?? rawData.paymentMethodsCount ?? 0,
              currentPeriodEnd: rawData.current_period_end || rawData.currentPeriodEnd || null,
              currentPeriodStart:
                rawData.current_period_start || rawData.currentPeriodStart || null,
              hasSubscription: rawData.has_subscription ?? rawData.hasSubscription ?? false,
              monthlyCostCents: rawData.monthly_cost_cents ?? rawData.monthlyCostCents ?? 0,
              discount: rawData.discount ?? null,
            };
            commit(SET_SUBSCRIPTION_STATUS, { data: normalizedData, isLoading: false });
            return normalizedData;
          }
          commit(SET_SUBSCRIPTION_STATUS, { data: null, isLoading: false });
          return null;
        } catch (error) {
          commit(SET_SUBSCRIPTION_STATUS, { data: null, isLoading: false });
          return null;
        }
      })();
      _subscriptionStatusPromise.finally(() => {
        _subscriptionStatusPromise = null;
      });
      return _subscriptionStatusPromise;
    },
    [START_FREE_TRIAL]: async ({ commit, dispatch }: any) => {
      try {
        const result: AxiosResponse = await Billing.startFreeTrial();
        if (result?.data?.status) {
          await dispatch(GET_TRIAL_STATUS, { forceRefresh: true });
          return true;
        }
        return false;
      } catch (error) {
        throw error;
      }
    },
    [GET_TRIAL_STATUS]: async (
      { commit, state, rootGetters }: any,
      payload?: { forceRefresh?: boolean }
    ) => {
      const cachedData = state.trialStatus.data;
      const cacheTimestamp = state.trialStatus.cacheTimestamp;
      const now = Date.now();
      const forceRefresh = payload?.forceRefresh === true;

      if (!forceRefresh && cachedData && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
        return cachedData;
      }

      if (!forceRefresh && _trialStatusPromise) return _trialStatusPromise;

      _trialStatusPromise = (async () => {
        commit(SET_TRIAL_STATUS, { isLoading: true });
        try {
          const result: AxiosResponse = await Billing.getTrialStatus();
          if (result?.data?.status) {
            const trialData = result.data.data;
            commit(SET_TRIAL_STATUS, { data: trialData, isLoading: false });

            const isInternalUser = rootGetters.isInternalUser;
            const hasOnboarded = isInternalUser || trialData.type !== "none";
            commit(SET_HAS_COMPLETED_ONBOARDING, hasOnboarded);
            return trialData;
          }
          commit(SET_TRIAL_STATUS, { data: null, isLoading: false });
          return null;
        } catch (error: any) {
          commit(SET_TRIAL_STATUS, { data: null, isLoading: false });
          if (error?.response?.status === 401 || error?.response?.status === 403) return null;
          throw error;
        }
      })();
      _trialStatusPromise.finally(() => {
        _trialStatusPromise = null;
      });
      return _trialStatusPromise;
    },
  },
};

export default subscription;
